import { create } from "zustand";
import type { AIState, ChatMessage, SiteSpecContent } from "@imonsite/shared";

interface ChatState {
  // State
  siteId: string | null;
  messages: ChatMessage[];
  currentSpec: Partial<SiteSpecContent>;
  aiState: AIState;
  isStreaming: boolean;
  streamingText: string;
  error: string | null;
  isConnected: boolean;

  // Actions
  setSiteId: (id: string) => void;
  setMessages: (messages: ChatMessage[]) => void;
  addMessage: (message: ChatMessage) => void;
  updateLastAssistantMessage: (content: string) => void;
  setSpec: (spec: Partial<SiteSpecContent>) => void;
  mergeSpec: (partial: Partial<SiteSpecContent>) => void;
  setAIState: (state: AIState) => void;
  setStreaming: (streaming: boolean) => void;
  appendStreamingText: (text: string) => void;
  clearStreamingText: () => void;
  setError: (error: string | null) => void;
  setConnected: (connected: boolean) => void;
  reset: () => void;
}

const initialState = {
  siteId: null,
  messages: [],
  currentSpec: {},
  aiState: "collect_basics" as AIState,
  isStreaming: false,
  streamingText: "",
  error: null,
  isConnected: false,
};

export const useChatStore = create<ChatState>((set) => ({
  ...initialState,

  setSiteId: (id) => set({ siteId: id }),

  setMessages: (messages) => set({ messages }),

  addMessage: (message) =>
    set((state) => ({
      messages: [...state.messages, message],
    })),

  updateLastAssistantMessage: (content) =>
    set((state) => {
      const messages = [...state.messages];
      const lastIndex = messages.length - 1;
      if (lastIndex >= 0 && messages[lastIndex].role === "assistant") {
        messages[lastIndex] = { ...messages[lastIndex], content };
      }
      return { messages };
    }),

  setSpec: (spec) => set({ currentSpec: spec }),

  mergeSpec: (partial) =>
    set((state) => ({
      currentSpec: { ...state.currentSpec, ...partial },
    })),

  setAIState: (aiState) => set({ aiState }),

  setStreaming: (isStreaming) => set({ isStreaming }),

  appendStreamingText: (text) =>
    set((state) => ({
      streamingText: state.streamingText + text,
    })),

  clearStreamingText: () => set({ streamingText: "" }),

  setError: (error) => set({ error }),

  setConnected: (isConnected) => set({ isConnected }),

  reset: () => set(initialState),
}));



